import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';

/** Public page — consumes the one-time token emailed by POST /auth/forgot-password. */
export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    setError('');
    if (!token) { setError('This reset link is missing its token — please use the link from your email.'); return; }
    if (!newPassword || !confirmPassword) { setError('Please fill in both password fields'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await api.resetPassword({ token, newPassword });
      setSuccess(true);
      setTimeout(() => nav('/login'), 1800);
    } catch (e: any) {
      setError(e.response?.data?.message || 'This reset link is invalid or has expired. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: 'linear-gradient(135deg, #02357A 0%, #015CA6 50%, #039089 100%)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-8 pt-8 pb-6" style={{ background: 'linear-gradient(135deg, #02357A, #015CA6)' }}>
            <div className="flex items-center gap-3">
              <span className="text-3xl">🔒</span>
              <div>
                <p className="text-white font-bold text-base leading-tight">Reset Password</p>
                <p className="text-white/60 text-xs">Choose a new password for your account</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-7">
            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-2">
                <span>⚠️</span>{error}
              </div>
            )}
            {success ? (
              <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm flex items-center gap-2">
                <span>✅</span>Password reset successful. Redirecting to login…
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Password</label>
                    <input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" autoFocus
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none" />
                    <p className="text-xs text-gray-400 mt-1">Minimum 8 characters.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm New Password</label>
                    <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password" onKeyDown={e => e.key === 'Enter' && submit()}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none" />
                  </div>
                </div>
                <button onClick={submit} disabled={loading}
                  className="w-full mt-6 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition-all hover:opacity-90 active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #02357A, #015CA6)' }}>
                  {loading ? 'Resetting…' : 'Reset Password →'}
                </button>
              </>
            )}
            <p className="text-center text-xs text-gray-400 mt-5">
              <Link to="/login" className="text-[#015CA6] hover:underline">Back to Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
